/**
 * @gamedate/core-unity — `window.FlexoBridge`, the SignalR interop the Unity WebGL build talks
 * through. ONE implementation for every front-end (V3 Track B / b-libs, architecture B3.1/B3.2).
 *
 * The Unity side (FlexoSignalR.cs + FlexoGame.jslib) does NOT open its own SignalR connection in
 * WebGL. The jslib delegates to window.FlexoBridge.{connectSignalR,disconnectSignalR,
 * sendSignalRMessage} and expects callbacks via SendMessage to the `FlexoSignalR` GameObject:
 *   OnSignalRConnected(connectionId) / OnSignalRDisconnected(error) / OnSignalRMessage(json)
 *
 * ── THE WIRE LAWS (pinned by libs/core-unity/tests/wire-laws.test.ts) ─────────────────────────
 *
 * L1. TRANSLATION TABLE, Unity invoke -> RoomHub method:
 *   JoinRoom(code,id,name,appearance)    -> JoinRoom(roomId, playerInfo)
 *   LeaveRoom(roomId)                    -> LeaveRoom(roomId)
 *   PlayerSpawned(roomId, json)          -> NotifyPlayerSpawned(roomId, json)
 *   UpdatePlayerPosition(roomId, json)   -> SyncPlayer(playerDataJson)   [string passthrough]
 *   UpdatePlayerAppearance(...)          -> UpdatePlayerAppearance(roomId, appearanceJson)
 *   GetRoomRoster / GetMySpawn / GetSceneConfiguration -> relayed back as EVENTS (L2)
 *   anything else                        -> invoked verbatim
 *
 * L2. NO HUB RETURN VALUES REACH WEBGL. Unity's InvokeAsync<T> returns default() under WebGL, so
 *   every "ask the server a question" call is answered by RELAYING an event back into Unity's own
 *   handler (RoomRosterResult / MySpawnResult / SceneConfigurationUpdated). Adding a `return`
 *   here silently produces an empty scene and an invisible roster.
 *
 * L3. RoomHub event -> Unity handler:
 *   PlayerJoinedRoom / PlayerReconnected -> PlayerJoinedRoom {roomId, payload}
 *   PlayerSpawned                        -> PlayerSpawned {roomId, payload}
 *   PlayerLeftRoom                       -> PlayerLeftRoom (userId only)
 *   ReceivePlayerSync                    -> PlayerPositionUpdate (unwrapped .flexo)
 *   PlayerAppearanceUpdated              -> PlayerAppearanceUpdated {roomId, userId, payload}
 *   SceneConfigurationUpdated / SocialEventReceived -> passthrough
 *
 * L4. Position updates ride a SignalRPlayerData-shaped envelope with the raw Unity payload under
 *   `flexo`, because the hub's movement model cannot round-trip System.Numerics types.
 *
 * L5. A player's own echo is dropped: ReceivePlayerSync and PlayerAppearanceUpdated for
 *   `localPlayerId` never reach Unity, or an avatar fights its own network copy.
 *
 * ── TENANT PROFILE ────────────────────────────────────────────────────────────────────────────
 * Everything tenant-specific is an OPTION, never a fork:
 *   tokenKey            which localStorage key holds the bearer token
 *   rejoinOnReconnect   SignalR groups do NOT survive a reconnect; the server puts a connection
 *                       into `room:{id}` inside JoinRoom, so automatic reconnect otherwise leaves
 *                       a client connected but deaf. Re-invoking JoinRoom is idempotent server
 *                       side (JoinRoomCore takes the "already in the room" branch and re-adds the
 *                       group). Defaults to `false` — the behaviour the dating bundle shipped —
 *                       so adopting this package changes nothing until a host opts in.
 * The observer taps (onSocialEvent/onLocalPosition/onRoster/onConnectionChange) are subscriptions
 * on traffic that already flows; with no subscribers they are inert.
 */

import * as signalR from '@microsoft/signalr';

type UnitySendMessage = (gameObjectName: string, methodName: string, parameter?: string | number) => void;

const SIGNALR_GO = 'FlexoSignalR';

export interface FlexoBridgeOptions {
  /** localStorage key holding the bearer token handed to the hub. */
  tokenKey: string;
  /** Re-invoke JoinRoom after an automatic reconnect (see TENANT PROFILE above). */
  rejoinOnReconnect: boolean;
}

/** Defaults = the profile the dating bundle has always run with. */
const options: FlexoBridgeOptions = {
  tokenKey: 'gamedate_token',
  rejoinOnReconnect: false,
};

/** Set the tenant profile. Call once, before the WASM boots. */
export function configureFlexoBridge(patch: Partial<FlexoBridgeOptions>): void {
  Object.assign(options, patch);
}

export function flexoBridgeOptions(): Readonly<FlexoBridgeOptions> {
  return { ...options };
}

export interface FlexoPositionUpdate {
  playerId: string;
  position?: { x: number; y: number; z: number };
  rotation?: number;
  velocityX?: number;
  velocityY?: number;
  velocityZ?: number;
  animState?: string;
  zone?: string;
  configId?: string;
}

/** The server-authoritative envelope every social/game broadcast arrives in. */
export interface SocialEventEnvelope {
  type?: string;
  payloadJson?: string;
  senderId?: string;
}

type Unsub = () => void;

class FlexoBridge {
  private connection: signalR.HubConnection | null = null;
  private unitySendMessage: UnitySendMessage | null = null;
  private connecting = false;
  private currentRoomId: string | null = null;
  private localPlayerId: string | null = null;
  private warned = new Set<string>();
  private spawnPushedRoomId: string | null = null;
  /** Remembered so a reconnect can re-enter the room GROUP (adaptation 3). */
  private lastJoin: { roomId: string; info: Record<string, string> } | null = null;

  private socialSubs = new Set<(env: SocialEventEnvelope) => void>();
  private positionSubs = new Set<(p: FlexoPositionUpdate) => void>();
  private connectionSubs = new Set<(connected: boolean) => void>();
  private rosterSubs = new Set<(roster: Array<{ id: string; name: string }>) => void>();
  /** Dungeon-master broadcasts, for a host surface that wants to narrate them. */
  private dmSubs = new Set<(messageJson: string) => void>();
  /**
   * Who else is standing in this room, excluding the pupil themselves.
   *
   * Fed by the JOIN/LEAVE broadcasts rather than only by the roster invoke, because the engine
   * asks for the roster at most once (for latecomer visibility) and a classmate who walks in
   * afterwards would never appear on the chip even though their avatar is plainly there.
   */
  private roster = new Map<string, string>();

  // ---- lifecycle from the React host ----

  attachUnity(sendMessage: UnitySendMessage) {
    this.unitySendMessage = sendMessage;
    this.warned.delete('unity-send-unavailable');
  }

  detachUnity() {
    this.unitySendMessage = null;
  }

  get connected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  // ---- observers (school adaptation 2) ----

  onSocialEvent(cb: (env: SocialEventEnvelope) => void): Unsub {
    this.socialSubs.add(cb);
    return () => this.socialSubs.delete(cb);
  }

  onLocalPosition(cb: (p: FlexoPositionUpdate) => void): Unsub {
    this.positionSubs.add(cb);
    return () => this.positionSubs.delete(cb);
  }

  onConnectionChange(cb: (connected: boolean) => void): Unsub {
    this.connectionSubs.add(cb);
    return () => this.connectionSubs.delete(cb);
  }

  /**
   * Who else is in the room.
   *
   * V3 b-libs(2) — REPLAYS the current roster to a new subscriber. A HUD that mounts after the
   * roster has already arrived would otherwise wait for the next CHANGE before it learned anything,
   * and the roster is a SET rather than a stream of deltas, so a subscriber handed the current
   * state and then every change is in exactly the position of one that listened from the start.
   *
   * HONEST SCOPE. This was written to fix an observed two-client symptom — the pupil who joined
   * SECOND showed an empty classmate chip while the pupil who joined FIRST showed "1" — and
   * DID NOT FIX IT. Re-tested on the deployed build: still empty for the late joiner. So the
   * bridge's roster map is genuinely empty for that client, which means the cause is upstream of
   * this subscription (the roster answer the late joiner receives does not contain the pupil
   * already standing there, and nothing asks again). That is recorded as an open bug rather than
   * papered over. The replay is kept because it is independently correct and tested (L7/L7b/L7c),
   * not because it closed the symptom.
   *
   * Both avatars DO render in the 3D world for both pupils throughout — the world is right and
   * only the HUD chip is wrong, which is why this survived unnoticed.
   */
  onRoster(cb: (roster: Array<{ id: string; name: string }>) => void): Unsub {
    this.rosterSubs.add(cb);
    if (this.roster.size > 0) cb([...this.roster.entries()].map(([id, name]) => ({ id, name })));
    return () => this.rosterSubs.delete(cb);
  }

  /** The dungeon master's channel — a teacher conducting a lesson, or a host running a room. */
  onDungeonMaster(cb: (messageJson: string) => void): Unsub {
    this.dmSubs.add(cb);
    return () => this.dmSubs.delete(cb);
  }

  /** Ask the hub something from the React side (e.g. RequestGameStates). Never throws. */
  async invoke(method: string, ...args: unknown[]): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
    try {
      await conn.invoke(method, ...args);
    } catch {
      /* the caller polls; a dropped poke is not an error worth a pupil's attention */
    }
  }

  // ---- jslib entry points (called from Unity WASM) ----

  connectSignalR = (url: string, token: string): void => {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      this.notifyConnected(this.connection.connectionId ?? '');
      return;
    }
    if (this.connecting) return;
    this.connecting = true;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => token || localStorage.getItem(options.tokenKey) || '',
      })
      .withAutomaticReconnect([0, 1000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection = connection;
    this.registerServerEvents(connection);

    connection.onreconnecting(() => {
      this.connectionSubs.forEach((cb) => cb(false));
      this.sendToUnity('OnSignalRDisconnected', 'reconnecting');
    });

    connection.onreconnected((connectionId) => {
      this.notifyConnected(connectionId ?? '');
      // WIRE LAW — groups do not survive a reconnect. Re-enter the room or the client is
      // connected, alone, and silently receiving nothing. Opt-in per tenant profile.
      if (options.rejoinOnReconnect) void this.rejoinAfterReconnect();
    });

    connection.onclose(() => {
      this.connecting = false;
      this.connectionSubs.forEach((cb) => cb(false));
      this.sendToUnity('OnSignalRDisconnected', 'closed');
    });

    connection
      .start()
      .then(() => {
        this.connecting = false;
        this.notifyConnected(connection.connectionId ?? '');
      })
      .catch((err) => {
        this.connecting = false;
        this.connectionSubs.forEach((cb) => cb(false));
        this.sendToUnity('OnSignalRDisconnected', String(err?.message ?? err));
      });
  };

  disconnectSignalR = (): void => {
    const conn = this.connection;
    this.connection = null;
    this.connecting = false;
    this.currentRoomId = null;
    this.lastJoin = null;
    conn?.stop().catch(() => undefined);
  };

  sendSignalRMessage = (method: string, argsJson: string): void => {
    let args: unknown[] = [];
    try {
      args = argsJson ? (JSON.parse(argsJson) as unknown[]) : [];
    } catch {
      return;
    }
    this.translateAndInvoke(method, args).catch(() => {
      /* one dropped frame is not worth a console wall in a classroom */
    });
  };

  getAuthToken = (): string => localStorage.getItem(options.tokenKey) ?? '';
  setAuthToken = (_token: string): void => undefined;
  clearAuthToken = (): void => undefined;

  private async rejoinAfterReconnect(): Promise<void> {
    const conn = this.connection;
    const join = this.lastJoin;
    if (!conn || !join || conn.state !== signalR.HubConnectionState.Connected) return;
    try {
      await conn.invoke('JoinRoom', join.roomId, join.info);
    } catch {
      /* the world's own reconnect path retries too */
    }
  }

  // ---- Unity -> hub translation ----

  private async translateAndInvoke(method: string, args: unknown[]): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;

    switch (method) {
      case 'JoinRoom': {
        const [roomCode, playerId, displayName, appearanceData] = args as string[];
        this.currentRoomId = roomCode;
        this.localPlayerId = playerId;
        this.spawnPushedRoomId = null;
        this.roster.clear();
        this.emitRoster();
        const info = { id: playerId, displayName, appearanceData: appearanceData ?? '' };
        this.lastJoin = { roomId: roomCode, info };
        await conn.invoke('JoinRoom', roomCode, info);
        return;
      }
      case 'LeaveRoom': {
        const [roomId] = args as string[];
        this.currentRoomId = null;
        this.spawnPushedRoomId = null;
        this.lastJoin = null;
        await conn.invoke('LeaveRoom', roomId);
        return;
      }
      case 'PlayerSpawned': {
        const [roomId, spawnJson] = args as string[];
        await conn.invoke('NotifyPlayerSpawned', roomId, spawnJson);
        return;
      }
      case 'UpdatePlayerPosition': {
        const [roomId, updateJson] = args as string[];
        let update: FlexoPositionUpdate;
        try {
          update = JSON.parse(updateJson) as FlexoPositionUpdate;
        } catch {
          return;
        }
        // School adaptation 2 — the pupil's own position drives the coordinate readout.
        // This is a TAP on a frame that is being sent anyway; nothing extra goes on the wire.
        this.positionSubs.forEach((cb) => cb(update));
        const envelope = {
          playerId: update.playerId,
          playerName: '',
          lobbyId: roomId,
          characterType: '',
          avatarGuid: '',
          position: update.position ?? { x: 0, y: 0, z: 0 },
          rotY: update.rotation ?? 0,
          zone: update.zone ?? '',
          configId: update.configId ?? '',
          flexo: update,
        };
        await conn.invoke('SyncPlayer', JSON.stringify(envelope));
        return;
      }
      case 'UpdatePlayerAppearance': {
        const strArgs = args as string[];
        const roomId = strArgs.length >= 3 ? strArgs[0] || this.currentRoomId || '' : this.currentRoomId ?? '';
        const appearanceJson = strArgs.length >= 3 ? strArgs[2] ?? '' : strArgs[1] ?? '';
        if (!roomId || !appearanceJson) return;
        try {
          await conn.invoke('UpdatePlayerAppearance', roomId, appearanceJson);
        } catch {
          this.warnOnce(method, '[FlexoBridge] UpdatePlayerAppearance not supported by this server');
        }
        return;
      }
      case 'GetRoomRoster': {
        // RELAY, not return (see WIRE LAW). Always answer, even on failure, so Unity's await
        // resolves instead of timing a late joiner out into an empty room.
        const [roomId] = args as string[];
        let rosterJson = '{"roomId":"","players":[]}';
        try {
          const result = await conn.invoke<string>('GetRoomRoster', roomId);
          if (result) rosterJson = result;
        } catch {
          this.warnOnce(method, '[FlexoBridge] GetRoomRoster not supported by this server');
        }
        this.publishRoster(rosterJson);
        this.forward('RoomRosterResult', rosterJson);
        return;
      }
      case 'GetMySpawn': {
        const [roomId] = args as string[];
        if (roomId && roomId === this.spawnPushedRoomId) return; // JoinRoom already pushed it
        let spawnJson = '{"hasRestore":false}';
        try {
          const result = await conn.invoke<string>('GetMySpawn', roomId);
          if (result) spawnJson = result;
        } catch {
          this.warnOnce(method, '[FlexoBridge] GetMySpawn not supported by this server');
        }
        this.forward('MySpawnResult', spawnJson);
        return;
      }
      case 'GetSceneConfiguration': {
        const [roomId, configId] = args as string[];
        const configJson = await conn.invoke<string>('GetSceneConfiguration', roomId, configId ?? '');
        if (configJson) {
          this.forward('SceneConfigurationUpdated', configJson);
          // The world arrives down THIS path far more often than down the server push — Unity asks
          // for it during its own boot. Publishing on only one of the two left the lesson overlay
          // with no world to read (observed: the coordinate readout worked, the target map was
          // empty). Both paths publish now.
          this.publishSceneConfig(configJson);
        }
        return;
      }
      default:
        await conn.invoke(method, ...args);
    }
  }

  // ---- hub -> Unity translation ----

  private registerServerEvents(conn: signalR.HubConnection) {
    conn.on('PlayerJoinedRoom', (roomId: string, playerJson: string) => {
      this.forward('PlayerJoinedRoom', JSON.stringify({ roomId, payload: playerJson }));
      this.rosterAdd(playerJson);
    });
    conn.on('PlayerReconnected', (roomId: string, playerJson: string) => {
      this.forward('PlayerJoinedRoom', JSON.stringify({ roomId, payload: playerJson }));
      this.rosterAdd(playerJson);
    });
    conn.on('PlayerSpawned', (roomId: string, spawnJson: string) => {
      this.forward('PlayerSpawned', JSON.stringify({ roomId, payload: spawnJson }));
    });
    conn.on('PlayerLeftRoom', (_roomId: string, userId: string) => {
      this.forward('PlayerLeftRoom', userId);
      if (userId && this.roster.delete(userId)) this.emitRoster();
    });
    conn.on('SceneConfigurationUpdated', (configJson: string) => {
      this.forward('SceneConfigurationUpdated', configJson);
      this.publishSceneConfig(configJson);
    });
    conn.on('MySpawnResult', (roomId: string, spawnJson: string) => {
      if (roomId && this.currentRoomId && roomId !== this.currentRoomId) return;
      this.spawnPushedRoomId = roomId || this.currentRoomId;
      this.forward('MySpawnResult', spawnJson);
    });
    conn.on('SocialEventReceived', (envelopeJson: string) => {
      this.forward('SocialEventReceived', envelopeJson);
      // School adaptation 2 — the same broadcast drives the lesson HUD.
      let env: SocialEventEnvelope;
      try {
        env = JSON.parse(envelopeJson) as SocialEventEnvelope;
      } catch {
        return;
      }
      this.socialSubs.forEach((cb) => cb(env));
    });
    conn.on('PlayerAppearanceUpdated', (roomId: string, userId: string, appearanceJson: string) => {
      if (userId && userId === this.localPlayerId) return;
      if (roomId && this.currentRoomId && roomId !== this.currentRoomId) return;
      this.forward('PlayerAppearanceUpdated', JSON.stringify({ roomId, userId, payload: appearanceJson }));
    });
    // ── DUNGEON MASTER relay ────────────────────────────────────────────────────────────────
    // The conductor's channel: a host (dating) or a teacher (school) drops an object into the
    // live room, removes it, or changes its state, and every client's Unity instance is told.
    // Pure relays, server -> Unity, with no tenant knowledge — which is why the SAME four events
    // serve a host running a party game and a teacher running a lesson.
    //
    // The school bundle's forked copy of this bridge had silently DROPPED all four, so a teacher
    // conducting a classroom world reached nobody. libs/tests/bridge-parity.test.ts is what
    // caught it, and is what stops it recurring.
    conn.on('DungeonMasterObjectSpawned', (objectDataJson: string) => {
      this.forward('DungeonMasterObjectSpawned', objectDataJson);
    });
    conn.on('DungeonMasterObjectDestroyed', (objectId: string) => {
      this.forward('DungeonMasterObjectDestroyed', objectId);
    });
    conn.on('DungeonMasterObjectStateChanged', (objectId: string, stateJson: string) => {
      // StateChanged's two args are packed into one JSON string for Unity's parser.
      this.forward('DungeonMasterObjectStateChanged', JSON.stringify({ objectId, stateJson }));
    });
    conn.on('DungeonMaster', (messageJson: string) => {
      this.forward('DungeonMaster', messageJson);
      // Observer tap: a lesson HUD wants to show what the teacher just conducted without
      // opening a second hub connection.
      this.dmSubs.forEach((cb) => cb(messageJson));
    });

    conn.on('ReceivePlayerSync', (playerDataJson: string) => {
      try {
        const data = JSON.parse(playerDataJson) as { playerId?: string; lobbyId?: string; flexo?: FlexoPositionUpdate };
        if (!data.flexo) return;
        if (data.playerId && data.playerId === this.localPlayerId) return;
        if (data.lobbyId && this.currentRoomId && data.lobbyId !== this.currentRoomId) return;
        this.forward('PlayerPositionUpdate', JSON.stringify(data.flexo));
      } catch {
        /* not a flexo envelope */
      }
    });
  }

  /**
   * Hand the authored world to the React side. Both the invoke-relay and the server-push carry it;
   * whichever lands first wins and the other is a harmless repeat (consumers key on object ids).
   */
  private publishSceneConfig(configJson: string) {
    window.dispatchEvent(new CustomEvent('qa-scene-config', { detail: configJson }));
  }

  private publishRoster(rosterJson: string) {
    try {
      const parsed = JSON.parse(rosterJson) as { players?: Array<{ id?: string; displayName?: string }> };
      let changed = false;
      for (const p of parsed.players ?? []) {
        const id = String(p.id ?? '');
        if (!id || id === this.localPlayerId) continue;
        const name = String(p.displayName ?? '');
        if (this.roster.get(id) !== name) { this.roster.set(id, name); changed = true; }
      }
      if (changed) this.emitRoster();
    } catch {
      /* roster display is best-effort — the world itself is the truth */
    }
  }

  /** One join/reconnect broadcast. The payload is the hub's camelCase player object. */
  private rosterAdd(playerJson: string) {
    try {
      const p = JSON.parse(playerJson) as { id?: string; userId?: string; displayName?: string; playerName?: string };
      const id = String(p.id ?? p.userId ?? '');
      if (!id || id === this.localPlayerId) return;
      const name = String(p.displayName ?? p.playerName ?? '');
      if (this.roster.get(id) === name) return;
      this.roster.set(id, name);
      this.emitRoster();
    } catch {
      /* best-effort */
    }
  }

  private emitRoster() {
    const list = [...this.roster.entries()].map(([id, name]) => ({ id, name }));
    this.rosterSubs.forEach((cb) => cb(list));
  }

  private forward(method: string, args: string) {
    this.sendToUnity('OnSignalRMessage', JSON.stringify({ method, args }));
  }

  private notifyConnected(connectionId: string) {
    this.connectionSubs.forEach((cb) => cb(true));
    this.sendToUnity('OnSignalRConnected', connectionId);
  }

  private sendToUnity(handler: string, parameter: string) {
    const send =
      this.unitySendMessage ?? (window as Window & { sendUnityMessage?: UnitySendMessage }).sendUnityMessage;
    if (!send) {
      this.warnOnce('unity-send-unavailable', '[FlexoBridge] world not mounted — dropping relayed frames');
      return;
    }
    try {
      send(SIGNALR_GO, handler, parameter);
    } catch {
      /* a dead Unity instance is handled by the host's error path */
    }
  }

  private warnOnce(key: string, message: string) {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    console.warn(message);
  }
}

export const flexoBridge = new FlexoBridge();

/**
 * Install window.FlexoBridge BEFORE the WASM boots. Idempotent.
 *
 * NOT called at import time (the school bundle used to): a host must be able to call
 * `configureFlexoBridge` first, and a module that reaches for `window` on import cannot be
 * imported by a test or by SSR.
 */
export function installFlexoBridge(): void {
  const w = window as Window & { FlexoBridge?: unknown };
  if (w.FlexoBridge === flexoBridge) return;
  w.FlexoBridge = flexoBridge;
}

